import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, useAnimations } from '@react-three/drei';
import { Suspense, useEffect, useState, useRef } from 'react';
import './Avatar3D.css';

interface Avatar3DFiberProps {
  character?: string;
  className?: string;
  isTalking?: boolean;
  isChatVisible?: boolean;
}

interface AvatarModelProps {
  character?: string;
  isMobilePortrait?: boolean;
}

function AvatarModel({ character, isMobilePortrait }: AvatarModelProps) {
  const modelPath = character?.toLowerCase().includes('nia') 
    ? '/assets/avatars/nia.glb' 
    : '/assets/avatars/ian.glb';

  // Cargar animación idle
  const idleAnimPath = character?.toLowerCase().includes('nia')
    ? '/assets/avatars/animations/nia-idle.glb'
    : '/assets/avatars/animations/ian-idle.glb';

  const gltf = useGLTF(modelPath);
  const idleGltf = useGLTF(idleAnimPath);
  
  // Posición del avatar según dispositivo
  const avatarPosition = isMobilePortrait
    ? [0, -0.4, 3] as [number, number, number]      // MÓVIL VERTICAL
    : [0.1, -1.05, 0.5] as [number, number, number]; // PC
    
  const meshesRef = useRef<any[]>([]);
  const { actions } = useAnimations(idleGltf.animations, gltf.scene);
  const blinkRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioSourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const analyserDataRef = useRef<Uint8Array | null>(null);
  const mouthOpenRef = useRef(0);

  useEffect(() => {
    // Buscar meshes con morph targets
    const meshes: any[] = [];
    gltf.scene.traverse((node: any) => {
      if (node.isMesh && node.morphTargetDictionary && node.morphTargetInfluences) {
        meshes.push(node);
      }
    });
    meshesRef.current = meshes;
    // console.log('[Avatar3DFiber] ✅ Meshes con morph targets encontrados:', meshes.length);
  }, [gltf.scene]);

  // Reproducir animación idle
  useEffect(() => {
    if (actions && Object.keys(actions).length > 0) {
      const actionName = Object.keys(actions)[0];
      const action = actions[actionName];
      if (action) {
        action.reset().fadeIn(0.8).play();
        action.setLoop(2201, Infinity); // Loop infinito
        // console.log('[Avatar3DFiber] ✅ Animación idle reproduciendo:', actionName);
      }
    }
  }, [actions]);

  // Parpadeo automático
  useEffect(() => {
    let blinkTimeout: number | undefined;
    let blinkResetTimeout: number | undefined;

    const scheduleBlink = () => {
      const nextDelay = Math.floor(1000 + Math.random() * 4000);
      blinkTimeout = window.setTimeout(() => {
        blinkRef.current = true;
        blinkResetTimeout = window.setTimeout(() => {
          blinkRef.current = false;
          scheduleBlink();
        }, 180);
      }, nextDelay);
    };

    scheduleBlink();

    return () => {
      if (blinkTimeout) window.clearTimeout(blinkTimeout);
      if (blinkResetTimeout) window.clearTimeout(blinkResetTimeout);
    };
  }, []);

  // Audio events
  useEffect(() => {
    const handleAudioStart = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const audioElement = detail?.audioElement as HTMLAudioElement | undefined;
      if (!audioElement) return;

      if (audioRef.current === audioElement && analyserRef.current) {
        return;
      }

      audioRef.current = audioElement;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }

      const context = audioContextRef.current;
      if (context.state === 'suspended') {
        context.resume().catch(() => undefined);
      }

      if (audioSourceRef.current) {
        try {
          audioSourceRef.current.disconnect();
        } catch {}
      }

      try {
        audioSourceRef.current = context.createMediaElementSource(audioElement);
        analyserRef.current = context.createAnalyser();
        analyserRef.current.fftSize = 512;
        analyserDataRef.current = new Uint8Array(analyserRef.current.fftSize);
        audioSourceRef.current.connect(analyserRef.current);
        analyserRef.current.connect(context.destination);
      } catch (error) {
        // Audio ya conectado
      }
    };

    const handleAudioEnd = () => {
      audioRef.current = null;
      analyserRef.current = null;
      analyserDataRef.current = null;
      if (audioSourceRef.current) {
        try {
          audioSourceRef.current.disconnect();
        } catch {}
        audioSourceRef.current = null;
      }
      mouthOpenRef.current = 0;
    };

    document.addEventListener('avatarAudioStart', handleAudioStart);
    document.addEventListener('avatarAudioEnd', handleAudioEnd);

    return () => {
      document.removeEventListener('avatarAudioStart', handleAudioStart);
      document.removeEventListener('avatarAudioEnd', handleAudioEnd);
    };
  }, []);

  // Animation loop
  useFrame(() => {
    const lerp = (current: number, target: number, speed: number) =>
      current + (target - current) * speed;

    const applyMorph = (target: string, value: number, speed: number) => {
      meshesRef.current.forEach((mesh) => {
        const index = mesh.morphTargetDictionary?.[target];
        if (index !== undefined && mesh.morphTargetInfluences) {
          const current = mesh.morphTargetInfluences[index] ?? 0;
          mesh.morphTargetInfluences[index] = lerp(current, value, speed);
        }
      });
    };

    // Parpadeo
    const blinkValue = blinkRef.current ? 1 : 0;
    applyMorph('eyeBlinkLeft', blinkValue, 0.5);
    applyMorph('eyeBlinkRight', blinkValue, 0.5);

    // Lipsync
    let mouthTarget = 0;
    const analyser = analyserRef.current;
    const audioElement = audioRef.current;
    const data = analyserDataRef.current;

    if (analyser && audioElement && !audioElement.paused && !audioElement.ended && data) {
      // @ts-ignore - TypeScript issue with Uint8Array types
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const normalized = (data[i] - 128) / 128;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / data.length);
      const normalized = Math.max(0, (rms - 0.03) / 0.45);
      mouthTarget = Math.min(normalized, 1);
    }

    mouthOpenRef.current = lerp(mouthOpenRef.current, mouthTarget, 0.3);
    const mouthValue = mouthOpenRef.current;

    applyMorph('jawOpen', mouthValue, 0.3);
    applyMorph('viseme_aa', mouthValue, 0.1);
    applyMorph('viseme_sil', 5.0 - mouthValue, 1.0);
    applyMorph('viseme_O', mouthValue * 0.1, 0.4);
    applyMorph('viseme_ee', mouthValue * 0.3, 0.1);
    applyMorph('viseme_PP', mouthValue * 0.1, 0.1);
    applyMorph('viseme_FF', mouthValue * 0.15, 0.1);
  });

  return (
    <primitive 
      object={gltf.scene} 
      scale={1}
      position={avatarPosition}
    />
  );
}

// Loading component
function LoadingAvatar() {
  return (
    <mesh>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="lightblue" />
    </mesh>
  );
}

export function Avatar3DFiber({ character, className, isTalking = false, isChatVisible = false }: Avatar3DFiberProps) {
  // Detectar móvil vertical de forma reactiva
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      // Pequeño delay para asegurar que el navegador haya actualizado las dimensiones
      setTimeout(() => {
        const isPortrait = window.matchMedia('(max-width: 768px) and (orientation: portrait)').matches;
        setIsMobilePortrait(isPortrait);
      }, 100);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('orientationchange', checkMobile);
    
    // También escuchar cambios de orientación en dispositivos móviles
    if (screen.orientation) {
      screen.orientation.addEventListener('change', checkMobile);
    }

    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', checkMobile);
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', checkMobile);
      }
    };
  }, []);

  // Configuración de cámara según dispositivo
  // PC: Vista frontal fija como en la imagen
  const cameraPosition = isMobilePortrait
    ? [0, 1.2, 5.5] as [number, number, number]
    : [0, 0.8, 2.5] as [number, number, number];

  const cameraFov = isMobilePortrait ? 46 : 35;

  // console.log('🎬 Avatar3DFiber renderizando, character:', character);

  return (
    <div className={`avatar-3d ${isChatVisible ? 'chat-visible' : ''} ${className || ''}`}>
      <Canvas
        key={`avatar-canvas-${isMobilePortrait ? 'portrait' : 'landscape'}`}
        camera={{ 
          position: cameraPosition, 
          fov: cameraFov 
        }}
        style={{ 
          background: 'transparent',
          width: '100%',
          height: '100%'
        }}
      >
        {/* Luces */}
        <ambientLight intensity={0.8} />
        <directionalLight position={[5, 5, 5]} intensity={1.2} />
        <directionalLight position={[-5, 3, -5]} intensity={0.5} />

        {/* Modelo del avatar */}
        <Suspense fallback={<LoadingAvatar />}>
          <AvatarModel character={character} isMobilePortrait={isMobilePortrait} />
        </Suspense>
      </Canvas>
    </div>
  );
}

// Precargar modelos y animaciones
useGLTF.preload('/assets/avatars/nia.glb');
useGLTF.preload('/assets/avatars/ian.glb');
useGLTF.preload('/assets/avatars/animations/nia-idle.glb');
useGLTF.preload('/assets/avatars/animations/ian-idle.glb');
